import { useState, useEffect, lazy, Suspense } from "react";
import { DOMAINS_DATA } from "./data/domains";
import { InternshipDomain } from "./types";
import {
  applyPageSeoAndSchemas,
  getCurrentYear,
} from "./utils/seoConfig";
import Navbar from "./components/Navbar";
import GeneralHomeHero from "./components/GeneralHomeHero";
import { Sparkles, MessageSquare, Code, Laptop, ArrowRight, ArrowUp, Phone } from "lucide-react";

const HomepageTracksGrid = lazy(() => import("./components/HomepageTracksGrid"));
const Features = lazy(() => import("./components/Features"));
const DomainOverview = lazy(() => import("./components/DomainOverview"));
const Technologies = lazy(() => import("./components/Technologies"));
const Projects = lazy(() => import("./components/Projects"));
const Process = lazy(() => import("./components/Process"));
const InternshipGallery = lazy(() => import("./components/InternshipGallery"));
const Testimonials = lazy(() => import("./components/Testimonials"));
const FaqSection = lazy(() => import("./components/FaqSection"));
const Footer = lazy(() => import("./components/Footer"));

const SeoConsole = lazy(() => import("./components/SeoConsole"));
const AboutUs = lazy(() => import("./components/AboutUs"));
const BrochureModal = lazy(() => import("./components/BrochureModal"));
const OurTeam = lazy(() => import("./components/OurTeam"));
const InternshipComparison = lazy(() => import("./components/InternshipComparison"));

/**
 * Returns the app route from the current URL, always lowercase, no leading slash.
 *   /              → ""
 *   /about         → "about"
 *   /madurai-internship → "madurai-internship"
 */
const getAppPath = (): string => {
  const path = window.location.pathname.toLowerCase();
  return path === "/" ? "" : path.replace(/^\//, "").replace(/\/$/, "");
};

/**
 * Build a root-relative URL for history.pushState / replaceState.
 *   nav("")        → "/"
 *   nav("about")   → "/about"
 *   nav("madurai") → "/madurai"
 */
const nav = (segment: string): string => (segment ? `/${segment}` : "/");

export default function App() {
  const [activeDomain, setActiveDomain] = useState<InternshipDomain>(DOMAINS_DATA[0]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"apply" | "brochure">("apply");
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [currentView, setCurrentView] = useState<"home" | "about" | "track">("home");

  // Monitor scroll height to show back-to-top button
  useEffect(() => {
    const checkScrollTop = () => {
      if (!showScrollTop && window.scrollY > 400) {
        setShowScrollTop(true);
      } else if (showScrollTop && window.scrollY <= 400) {
        setShowScrollTop(false);
      }
    };

    window.addEventListener("scroll", checkScrollTop);
    return () => window.removeEventListener("scroll", checkScrollTop);
  }, [showScrollTop]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };

  // Read path on mount & coordinate indexability for Google Bots
  useEffect(() => {
    const handlePopState = () => {
      const path = getAppPath();
      if (path === "about") {
        setCurrentView("about");
        return;
      }
      if (path) {
        const cleanSlug = path.endsWith("-internship") ? path.replace("-internship", "") : path;
        const matched = DOMAINS_DATA.find((d) => d.shortSlug === cleanSlug);
        if (matched) {
          setActiveDomain(matched);
          setCurrentView("track");
        }
      } else {
        setCurrentView((prev) => {
          if (prev === "track" || prev === "about") {
            return "home";
          }
          return prev;
        });
      }
    };

    // On initial mount, check if there's a valid domain path and navigate to it.
    const initialPath = getAppPath();
    if (initialPath === "about") {
      setCurrentView("about");
    } else if (initialPath) {
      const cleanSlug = initialPath.endsWith("-internship")
        ? initialPath.replace("-internship", "")
        : initialPath;
      const matched = DOMAINS_DATA.find((d) => d.shortSlug === cleanSlug);
      if (matched) {
        setActiveDomain(matched);
        setCurrentView("track");
      } else {
        // Unknown path — clear it and show home
        window.history.replaceState(null, "", nav(""));
        setCurrentView("home");
      }
    } else {
      setCurrentView("home");
    }

    // Listen to future path changes (browser back/forward, programmatic updates)
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // Sync state changes back to url path so Google can crawl distinct routes with key terms
  const handleSelectDomain = (domain: InternshipDomain) => {
    setActiveDomain(domain);
    window.history.pushState(null, "", nav(domain.shortSlug + "-internship"));
    setCurrentView("track");
  };

  // SEO METADATA INJECTION PARAMS
  useEffect(() => {
    const view = currentView === "about" ? "about" : currentView === "home" ? "home" : "track";
    applyPageSeoAndSchemas({
      view,
      activeDomain,
      allDomains: DOMAINS_DATA,
      year: getCurrentYear(),
    });
  }, [activeDomain, currentView]);

  const handleOpenModal = (mode: "apply" | "brochure") => {
    setModalMode(mode);
    setModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#FCFAF7] text-[#1E293B] selection:bg-orange-600 selection:text-white font-sans scroll-smooth relative overflow-x-hidden" id="app-root-container">
      
      {/* Custom Warm Glowing Backdrops Matching reference image */}
      <div className="absolute top-0 right-[-10%] w-[500px] h-[500px] bg-orange-500/10 rounded-full blur-[120px] pointer-events-none opacity-60 z-0"></div>
      <div className="absolute top-[40%] left-[-10%] w-[500px] h-[500px] bg-amber-500/8 rounded-full blur-[120px] pointer-events-none opacity-40 z-0"></div>
      <div className="absolute bottom-0 right-[10%] w-[500px] h-[500px] bg-[#FF5B00]/8 rounded-full blur-[120px] pointer-events-none opacity-40 z-0"></div>
      
      {/* 1. Glassmorphism Navigation Hub Banner */}
      <Navbar
        allDomains={DOMAINS_DATA}
        selectedDomain={activeDomain}
        onSelectDomain={handleSelectDomain}
        openModal={handleOpenModal}
        currentView={currentView}
        onChangeView={(view, targetId) => {
          setCurrentView(view);
          if (view === "about") {
            window.history.pushState(null, "", nav("about"));
          } else if (view === "home") {
            window.history.pushState(null, "", nav(""));
          }
          if (targetId) {
            setTimeout(() => {
              document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth' });
            }, 100);
          } else {
            scrollToTop();
          }
        }}
      />

      <main className="relative z-10">
        {currentView === "about" ? (
          <div className="animate-fade-in">
            <Suspense fallback={null}>
              <AboutUs 
                onBackToHome={() => {
                  setCurrentView("home");
                  window.history.pushState(null, "", nav(""));
                  scrollToTop();
                }} 
                openModal={handleOpenModal} 
                hideHero={true}
              />
            </Suspense>
          </div>
        ) : currentView === "home" ? (
          <div className="animate-fade-in" id="homepage-modular-content">
            {/* General Hero Banner showcasing overall developer advantages */}
            <GeneralHomeHero
              allDomains={DOMAINS_DATA}
              onSelectDomain={handleSelectDomain}
              openModal={handleOpenModal}
            />

            {/* Specialization Selection Grid */}
            <Suspense fallback={null}>
              <HomepageTracksGrid
                allDomains={DOMAINS_DATA}
                onSelectDomain={handleSelectDomain}
                onChangeView={setCurrentView}
              />
            </Suspense>

            {/* Integrated Hitasoft Profile detail segments */}
            <Suspense fallback={null}>
              <AboutUs
                hideBreadcrumbs={true}
                hideHero={true}
                openModal={handleOpenModal}
                onBackToHome={() => {
                  const el = document.getElementById("internships");
                  if (el) {
                    el.scrollIntoView({ behavior: "smooth", block: "start" });
                  } else {
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }
                }}
              />

              <Process />
              <InternshipGallery />
              <Testimonials />
              {/* <OurTeam /> */}
              <Features />
              <FaqSection domain={activeDomain} />
            </Suspense>
          </div>
        ) : (
          /* Specialized Track detail view. Displays ONLY features and curriculum about this specific domain! */
          <div key={activeDomain.id} className="animate-fade-in" id="specialized-track-details-only">
            
            {/* Compact Track Title & Low-profile Navigation - No massive image banner */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-4 relative z-10" id="compact-track-header">
              
              {/* Back breadcrumb navigation */}
              <nav className="mb-6 flex items-center gap-2 text-xs font-mono font-bold text-slate-400">
                <button 
                  onClick={() => {
                    setCurrentView("home");
                    window.history.pushState(null, "", nav(""));
                    scrollToTop();
                  }} 
                  className="hover:text-orange-650 transition-colors cursor-pointer uppercase flex items-center gap-1"
                >
                  ← Syllabus Tracks
                </button>
                <span className="text-slate-300">/</span>
                <span className="text-orange-600 uppercase font-extrabold">{activeDomain.title}</span>
              </nav>

              {/* Immersive Light-mode modern cinematic hero background image banner for Department Track */}
              <div className="relative rounded-[32px] overflow-hidden p-6 sm:p-10 lg:p-14 text-slate-950 shadow-xl mb-8 bg-gradient-to-tr from-[#FFFFFF] via-[#F8FAFC] to-[#F1F5F9] border border-slate-200" id={`department-hero-${activeDomain.id}`}>
                {/* Background Artwork Layer with Coding/SaaS patterns & a subtle soft low-opacity image representative of the track */}
                <div className="absolute inset-0 z-0 select-none pointer-events-none overflow-hidden">
                  <img
                    src={activeDomain.heroImage}
                    alt={`${activeDomain.title} department banner`}
                    className="w-full h-full object-cover opacity-[0.06] filter grayscale brightness-[1.02] contrast-[1.1] scale-102 pointer-events-none"
                    referrerPolicy="no-referrer"
                  />
                  {/* Glassmorphism gradient meshes to blend seamlessly and provide perfect coding/designer backdrop */}
                  <div className="absolute inset-0 bg-gradient-to-r from-[#FFFFFF] via-[#FFFFFF]/90 to-transparent"></div>
                  <div className="absolute -top-[20%] -right-[10%] w-[350px] h-[350px] rounded-full bg-gradient-to-br from-orange-400/10 to-[#2563EB]/10 blur-3xl opacity-70"></div>
                  <div className="absolute -bottom-[20%] left-[20%] w-[450px] h-[350px] rounded-full bg-gradient-to-tr from-amber-400/5 to-[#2563EB]/5 blur-3xl opacity-80"></div>
                  {/* Subtle Tech grid line accents */}
                  <div className="absolute inset-0 bg-[radial-gradient(#e2e8f0_1px,transparent_1px)] [background-size:24px_24px] opacity-40"></div>
                </div>

                <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
                  
                  {/* Left Column: Descriptive Department syllabus details with light visual contrast */}
                  <div className="lg:col-span-7 space-y-5">
                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-orange-500/10 border border-orange-500/20 rounded-full text-[10px] font-mono font-bold tracking-widest text-orange-650 uppercase whitespace-nowrap">
                      <span className="w-1.5 h-1.5 rounded-full bg-orange-650 animate-pulse"></span>
                      {activeDomain.availability}
                    </div>
                    
                    <h1 className="text-3xl sm:text-4xl md:text-5xl font-display font-black text-[#0F172A] tracking-tight leading-none">
                      Master {activeDomain.title.replace("Development", "").replace("Engineer", "").trim()}{" "}
                      <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#2563EB] to-orange-650 font-black">
                        Talent Track
                      </span>
                    </h1>
                    
                    <p className="text-xs font-mono tracking-wider text-[#2563EB] font-extrabold uppercase">
                      {activeDomain.subTitle}
                    </p>
                    
                    <p className="text-slate-600 text-xs sm:text-sm leading-relaxed font-semibold max-w-2xl">
                      {activeDomain.heroParagraph}
                    </p>

                    {/* Highly-visible White Floating Stats Segment */}
                    <div className="grid grid-cols-3 gap-3.5 pt-6 mt-4 border-t border-slate-200/60 max-w-xl">
                      <div className="bg-[#FFFFFF] p-3.5 rounded-2xl border border-slate-200/60 shadow-xs hover:shadow-md transition-all duration-300">
                        <div className="text-xl sm:text-2xl font-display font-black text-[#0F172A] leading-tight">
                          {activeDomain.stats.studentsTrained}
                        </div>
                        <div className="text-[9px] uppercase font-bold tracking-tight text-slate-500 font-mono mt-1">Successful Alumni</div>
                      </div>
                      <div className="bg-[#FFFFFF] p-3.5 rounded-2xl border border-slate-200/60 shadow-xs hover:shadow-md transition-all duration-300">
                        <div className="text-xl sm:text-2xl font-display font-black text-[#0F172A] leading-tight">
                          {activeDomain.stats.projectsCompleted}
                        </div>
                        <div className="text-[9px] uppercase font-bold tracking-tight text-slate-500 font-mono mt-1">Hands-On Projects Delivered</div>
                      </div>
                      <div className="bg-[#FFFFFF] p-3.5 rounded-2xl border border-slate-200/60 shadow-xs hover:shadow-md transition-all duration-300">
                        <div className="text-xl sm:text-2xl font-display font-black text-[#2563EB] leading-tight">
                          {activeDomain.stats.placementRate}
                        </div>
                        <div className="text-[9px] uppercase font-bold tracking-tight text-[#2563EB] font-mono mt-1">Career Guidance & Placement Support</div>
                      </div>
                    </div>
                  </div>

                  {/* Right Column: Dynamic Light action & Admissions board inside a clean rounded card */}
                  <div className="lg:col-span-5 flex flex-col justify-center items-end max-md:items-center">
                    <div className="w-full bg-white/95 border border-slate-200 p-6 sm:p-7 rounded-3xl flex flex-col gap-4 shadow-xl shadow-slate-100/50 max-w-sm transition-all duration-300 hover:-translate-y-1.5 hover:scale-[1.018] hover:shadow-2xl hover:shadow-slate-200/80">
                      <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                        <span className="text-[10px] font-mono uppercase tracking-widest text-orange-650 font-extrabold flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-orange-650 animate-ping"></span>
                          Admissions Open
                        </span>
                        <span className="text-[9px] font-mono bg-slate-100 text-slate-700 px-2 py-0.5 rounded-lg border border-slate-200 font-extrabold uppercase tracking-widest">
                          {activeDomain.shortSlug}
                        </span>
                      </div>

                      {/* Admissions Data Table block */}
                      <div className="space-y-2.5 text-xs text-slate-600 border-b border-slate-100 pb-4">
                        <div className="flex justify-between items-center">
                          <span className="font-semibold text-slate-400 font-mono text-[9px] uppercase">Learning Format:</span>
                          <span className="font-extrabold text-slate-800 text-right max-w-[140px] leading-tight">Hybrid Training with Practical Labs</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="font-semibold text-slate-400 font-mono text-[9px] uppercase">Certification:</span>
                          <span className="font-extrabold text-emerald-600 flex items-center gap-0.5 text-right max-w-[140px] leading-tight">Certificate of Completion Included</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="font-semibold text-slate-400 font-mono text-[9px] uppercase">Batch Size:</span>
                          <span className="font-extrabold text-orange-600 text-right max-w-[140px] leading-tight">Limited Cohort Enrollment</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="font-semibold text-slate-400 font-mono text-[9px] uppercase">Duration:</span>
                          <span className="font-extrabold text-slate-800 text-right">{activeDomain.duration}</span>
                        </div>
                      </div>
                      
                      <div className="flex flex-col gap-2.5 pt-1">
                        <button
                          onClick={() => handleOpenModal("apply")}
                          className="w-full px-5 py-4 bg-gradient-to-r from-orange-650 to-orange-700 hover:from-orange-700 hover:to-orange-850 text-white font-bold text-xs tracking-wider uppercase rounded-xl transition-all duration-200 text-center cursor-pointer shadow-lg shadow-orange-500/10 hover:-translate-y-[1px] active:translate-y-0"
                        >
                          Join the Next Cohort
                        </button>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>

            <Suspense fallback={null}>
              {/* Track-specific technical syllabus chapters */}
              <DomainOverview domain={activeDomain} />

              {/* Track-specific technology tools stack */}
              <Technologies domain={activeDomain} />

              {/* Track-specific structural hands-on modules */}
              <Projects domain={activeDomain} />

              {/* Track-specific admissions FAQ board */}
              <FaqSection domain={activeDomain} />

              {/* Track-specific local verified SEO indexed checkmarks */}
              <SeoConsole domain={activeDomain} />
            </Suspense>

            {/* Intuitive return toggle at bottom */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex justify-center border-t border-slate-200/50">
              <button
                onClick={() => {
                  setCurrentView("home");
                  window.history.pushState(null, "", nav(""));
                  scrollToTop();
                }}
                className="flex items-center gap-2 px-6 py-3.5 bg-white border border-slate-300 hover:border-orange-500/35 hover:text-orange-650 rounded-xl text-xs font-bold text-slate-700 transition-all cursor-pointer shadow-xs"
                id="back-to-tracks-bottom-trigger"
              >
                ← Return to All Syllabus Tracks
              </button>
            </div>

          </div>
        )}
      </main>

      {/* 10. Global Footer incorporating CTA banners & Sitemap direct linkages */}
      <Suspense fallback={null}>
        <Footer
          allDomains={DOMAINS_DATA}
          selectedDomain={activeDomain}
          onSelectDomain={handleSelectDomain}
          openModal={handleOpenModal}
          onChangeView={(view) => {
            setCurrentView(view);
            if (view === "about") {
              window.history.pushState(null, "", nav("about"));
            } else if (view === "home") {
              window.history.pushState(null, "", nav(""));
            }
            scrollToTop();
          }}
        />
      </Suspense>

      {/* 11. Conversions Trigger Dialog System */}
      <Suspense fallback={null}>
        <BrochureModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          currentDomain={activeDomain}
          allDomains={DOMAINS_DATA}
          initialMode={modalMode}
        />
      </Suspense>

      {/* Floating dynamic Back to Top bubble */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-6 right-6 z-40 p-4 bg-white/95 hover:bg-slate-50 text-orange-600 hover:text-orange-700 rounded-full shadow-lg shadow-orange-500/5 transition-all duration-300 hover:scale-110 active:scale-95 flex items-center justify-center border border-slate-200 hover:border-orange-500/40 group cursor-pointer backdrop-blur-md ${
          showScrollTop ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-4 pointer-events-none"
        }`}
        title="Scroll back to top"
        id="back-to-top-floating-widget"
      >
        <ArrowUp className="w-5 h-5 animate-pulse" />
        <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 text-[10px] font-bold font-sans tracking-wide uppercase group-hover:ml-2 whitespace-nowrap text-slate-800">
          Back to Top
        </span>
      </button>

      {/* Persistent dynamic Contact widgets */}
      <div className="fixed top-1/2 -translate-y-1/2 right-6 z-40 flex flex-col gap-4">
        <a
          href="tel:+917708006989"
          className="p-4 bg-blue-600 hover:bg-blue-500 rounded-full shadow-2xl shadow-blue-500/20 text-white transition-all hover:scale-110 active:scale-95 flex items-center justify-center border border-blue-400/20 group cursor-pointer"
          title="Call Us"
          id="phone-fixed-floating-bubble"
        >
          <Phone className="w-6 h-6" />
          <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 text-[10px] font-bold font-sans tracking-wide uppercase group-hover:ml-2 whitespace-nowrap">
            Call Us
          </span>
        </a>
        <a
          href="https://wa.me/917708006989?text=Hi%20Hitasoft!%20I%27d%20like%20to%20talk%20to%20an%20expert."
          target="_blank"
          rel="noopener noreferrer"
          className="p-4 bg-[#25D366] hover:bg-[#20bc5a] rounded-full shadow-2xl shadow-green-500/20 text-white transition-all hover:scale-110 active:scale-95 flex items-center justify-center border border-green-400/20 group cursor-pointer"
          title="Chat on WhatsApp"
          id="whatsapp-fixed-floating-bubble"
        >
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 text-[10px] font-bold font-sans tracking-wide uppercase group-hover:ml-2 whitespace-nowrap">
            Talk to Experts
          </span>
        </a>
      </div>

    </div>
  );
}
